import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { Dialog } from '@/components/ui/layout';
import { Alert, Button, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import type { Role } from './types';

export function CloneRoleDialog({ role, onClose }: { role: Role; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState(`${role.name} (copy)`);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const clone = useMutation({
    mutationFn: () => post(`/org/roles/${role.id}/clone`, { code: code.trim() || undefined, name }),
    onSuccess: () => {
      toast.success(tr('admin', 'x_created', { name: name }), tr('admin', 'adjust_its_permissions_from_the_roles_list'));
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void queryClient.invalidateQueries({ queryKey: ['role-groups'] });
      onClose();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={tr('admin', 'duplicate_x', { name: role.name })}
      description={tr('admin', 'the_copy_starts_with_the_same_permissions_and')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('admin', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              clone.mutate();
            }}
            loading={clone.isPending}
            disabled={!name.trim() || (code.trim() !== '' && !/^[a-z0-9_]{2,40}$/.test(code))}
          >
            {tr('admin', 'duplicate')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <Field label={tr('admin', 'name')} required>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label={tr('admin', 'code')} hint={tr('admin', 'optional_lowercase_no_spaces_made_from_the_name')}>
          <Input value={code} onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
